import type { CsvExportResult } from '../types'

const MAX_CSV_ROWS = 100000
const MAX_CSV_SIZE_BYTES = 50 * 1024 * 1024

// Spreadsheets read a cell as a formula when it starts with = + - @, or with
// a tab or carriage return (OWASP CSV injection); a leading ' keeps it text.
const FORMULA_START = /^[\t\r]|^[\t\r ]*[=+\-@]/

const escapeCsvValue = (value: any): string => {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue = String(value)
  const sanitizedValue = FORMULA_START.test(stringValue)
    ? `'${stringValue}`
    : stringValue

  if (
    sanitizedValue.includes(',') ||
    sanitizedValue.includes('"') ||
    sanitizedValue.includes('\n') ||
    sanitizedValue.includes('\r')
  ) {
    return `"${sanitizedValue.replace(/"/g, '""')}"`
  }

  return sanitizedValue
}

const objectsToCsv = (data: Record<string, any>[]): string => {
  if (data.length === 0) {
    return ''
  }

  const headers = Object.keys(data[0])
  const headerRow = headers.map(escapeCsvValue).join(',')

  const dataRows = data.map((row) =>
    headers.map((header) => escapeCsvValue(row[header])).join(','),
  )

  return [headerRow, ...dataRows].join('\n')
}

const sanitizeFilename = (filename: string): string => {
  return filename.replace(/[^a-z0-9_-]/gi, '_').substring(0, 200)
}

export const getExportTimestamp = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`
}

export const downloadCsv = (
  data: Record<string, any>[],
  filename: string,
): CsvExportResult => {
  try {
    if (!data || data.length === 0) {
      return {
        success: false,
        error: 'No data available to export',
      }
    }

    if (data.length > MAX_CSV_ROWS) {
      return {
        success: false,
        error: `Dataset too large (${data.length.toLocaleString()} rows). Maximum allowed is ${MAX_CSV_ROWS.toLocaleString()} rows.`,
      }
    }

    const csvString = objectsToCsv(data)

    const sizeInBytes = new Blob([csvString]).size
    if (sizeInBytes > MAX_CSV_SIZE_BYTES) {
      return {
        success: false,
        error: `File size too large (${(sizeInBytes / 1024 / 1024).toFixed(1)}MB). Maximum allowed is ${MAX_CSV_SIZE_BYTES / 1024 / 1024}MB.`,
      }
    }

    const BOM = '\ufeff'
    const csvWithBom = BOM + csvString

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const sanitizedFilename = sanitizeFilename(filename)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${sanitizedFilename}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    return {
      success: true,
      rowCount: data.length,
    }
  } catch (error) {
    console.error('CSV export error:', error)
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while exporting',
    }
  }
}
